/**
 * outcomeTemplates: Hyperliquid's own title formats ("{perp} above {threshold} at {time}?"), keyword types and
 * categories for each template id. A snapshot is bundled (templates.snapshot.ts) so titles render offline and in
 * tests; the live list for the current env (useOutcomeTemplates) is merged over it so new templates just work.
 * Template ids are versioned per network (binaryPrice6, sportsContestWinner7, …); familyOf() strips the version.
 */
import type { EnvName } from '../config/networks';
import { TEMPLATE_SNAPSHOT } from './templates.snapshot';

export type TemplateRole = 'outcome' | 'question' | 'questionOutcome';

/** Keyword types seen live: string, shortString, hlPerp, uDecimal, uInt, dateTime. */
export type KeywordType = string;

export interface TemplateInfo {
  /** Title format with {keyword} placeholders. */
  name: string;
  role: TemplateRole;
  /** questionOutcome: the parent question template id. */
  parent?: string;
  /** Standalone outcomes: side name formats. */
  sideNames?: string[];
  keywords: Record<string, KeywordType>;
  /** From the template's metadata: price, sports, economics, culture, technology, … */
  category?: string;
}

export interface TemplateSnapshot {
  common: Record<string, TemplateInfo>;
  mainnet: Record<string, TemplateInfo>;
  testnet: Record<string, TemplateInfo>;
}

export type TemplateRegistry = ReadonlyMap<string, TemplateInfo>;

/** Raw element of POST /info {"type":"outcomeTemplates"}. */
export interface OutcomeTemplateRaw {
  id: string;
  role: 'question' | { standaloneOutcome?: { sideNames?: string[] }; questionOutcome?: { parent?: string } } | unknown;
  name: string;
  description?: string;
  keywords?: [string, string][];
}

/** Compact form of one live template; null when the element is malformed. */
export function compactTemplate(t: unknown): [string, TemplateInfo] | null {
  if (!t || typeof t !== 'object') return null;
  const r = t as OutcomeTemplateRaw;
  if (typeof r.id !== 'string' || typeof r.name !== 'string') return null;
  const roleObj = r.role && typeof r.role === 'object' ? (r.role as Record<string, Record<string, unknown>>) : null;
  const role: TemplateRole = r.role === 'question' ? 'question' : roleObj?.questionOutcome ? 'questionOutcome' : 'outcome';
  const info: TemplateInfo = { name: r.name, role, keywords: {} };
  const parent = roleObj?.questionOutcome?.parent;
  if (role === 'questionOutcome' && typeof parent === 'string') info.parent = parent;
  const sides = roleObj?.standaloneOutcome?.sideNames;
  if (Array.isArray(sides)) info.sideNames = sides.filter((s): s is string => typeof s === 'string');
  if (Array.isArray(r.keywords)) {
    for (const kw of r.keywords) if (Array.isArray(kw) && typeof kw[0] === 'string') info.keywords[kw[0]] = String(kw[1] ?? 'string');
  }
  const cat = /metadata=category:([^|]+)/.exec(typeof r.description === 'string' ? r.description : '');
  if (cat?.[1]) info.category = cat[1].trim();
  return [r.id, info];
}

const snapshotCache = new Map<EnvName, TemplateRegistry>();

/** The bundled snapshot for an env. */
export function snapshotRegistry(env: EnvName = 'mainnet'): TemplateRegistry {
  let r = snapshotCache.get(env);
  if (!r) {
    r = new Map(Object.entries({ ...TEMPLATE_SNAPSHOT.common, ...TEMPLATE_SNAPSHOT[env] }));
    snapshotCache.set(env, r);
  }
  return r;
}

/** Snapshot for `env` with the live outcomeTemplates list (if any) merged over it. */
export function buildRegistry(env: EnvName, live?: readonly unknown[] | null): TemplateRegistry {
  const base = snapshotRegistry(env);
  if (!live || live.length === 0) return base;
  const m = new Map(base);
  for (const t of live) {
    const c = compactTemplate(t);
    if (c) m.set(c[0], c[1]);
  }
  return m;
}

/** "template:binaryPrice6" → "binaryPrice6"; non-template names → null. */
export function templateId(name: string | null | undefined): string | null {
  if (typeof name !== 'string' || !name.startsWith('template:')) return null;
  const id = name.slice('template:'.length).trim();
  return id || null;
}

/** Versionless family: "binaryPrice6" → "binaryPrice", "sportsContestWinner7" → "sportsContestWinner". */
export function familyOf(id: string): string {
  return id.replace(/\d+$/, '');
}
